import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { ExtractionItem, ExtractionItemLookup } from "./extraction-citation-utils"
import type { ScoringSnippet, SourceRef, SourceSnippet } from "../../types"

import { Eye, FileText } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

import { normalizeCitationId, parseExtractionCitations } from "./extraction-citation-utils"
import { knownPage } from "../../utils/source-page"

export type { DocumentNameLookup, ExtractionItemLookup } from "./extraction-citation-utils"

export type OpenSourceFn = (source: SourceRef | SourceSnippet) => void

// ── Source button / display ───────────────────────────────────────────────────

type SourceButtonProps = {
  documentName: string
  page?: number | null
  navigableTo?: SourceRef | SourceSnippet
  onOpenSource?: OpenSourceFn
}

export function SourceButton({ documentName, page, navigableTo, onOpenSource }: SourceButtonProps) {
  const { t } = useTranslation()
  const canNavigate = Boolean(navigableTo && onOpenSource)
  const shownPage = knownPage(page)
  const openSource = () => {
    if (!canNavigate || !navigableTo || !onOpenSource) return
    onOpenSource(navigableTo)
  }

  const inner = (
    <>
      <FileText className="text-muted-foreground mt-0.5 size-3.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-foreground truncate text-xs font-medium">{documentName}</p>
        {shownPage !== undefined && (
          <p className="text-muted-foreground text-[11px]">
            {t("tender.snippet.page", { page: shownPage })}
          </p>
        )}
      </div>
      {canNavigate && (
        <Eye className="text-muted-foreground ml-auto size-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
      )}
    </>
  )

  if (canNavigate) {
    return (
      <Button
        className="group h-auto w-full justify-start gap-1.5 p-0 text-left font-normal"
        type="button"
        variant="ghost"
        onClick={openSource}
      >
        {inner}
      </Button>
    )
  }

  return (
    <div
      className="flex items-start gap-1.5"
      title={t("tender.extractionCitation.previewUnavailableTitle")}
    >
      {inner}
    </div>
  )
}

// ── Citation card (popover content) ──────────────────────────────────────────

const URGENCY_CLASS: Record<string, string> = {
  kritisk: "border-destructive bg-destructive/10 text-destructive",
  varning: "border-warning bg-warning/10 text-warning-foreground",
  normal: "border-border bg-muted/50 text-muted-foreground",
}

const RISK_CLASS: Record<string, string> = {
  lag: "border-success bg-success/10 text-success",
  medel: "border-warning bg-warning/10 text-warning-foreground",
  hog: "border-destructive bg-destructive/10 text-destructive",
}

export function ExtractionCitationCard({
  item,
  onOpenSource,
}: {
  item: ExtractionItem
  onOpenSource?: OpenSourceFn
}) {
  const { t } = useTranslation()
  const typeLabel = t(`tender.extractionCitation.types.${item.type}`)
  const riskClass = item.riskLevel ? RISK_CLASS[item.riskLevel] : undefined
  const riskLabel = item.riskLevel
    ? t(`tender.extractionCitation.risk.${item.riskLevel}`)
    : undefined
  const urgencyClass = item.urgency ? URGENCY_CLASS[item.urgency] : undefined
  const urgencyLabel = item.urgency
    ? t(`tender.extractionCitation.urgency.${item.urgency}`)
    : undefined

  const hasBadges =
    item.category ||
    item.isMandatory ||
    riskClass ||
    urgencyClass ||
    item.extra?.weightPercent != null ||
    item.extra?.quantity != null ||
    item.extra?.date != null

  let navigableSource: SourceRef | SourceSnippet | undefined
  if (item.snippet?.kind === "file") {
    navigableSource = item.snippet
  } else if (item.source?.kind === "file" && item.source.documentId) {
    navigableSource = item.source
  }

  const sourceDocName = item.source?.documentName ?? item.snippet?.documentName
  // The source's own page, else the snippet's page when it is from the same
  // document. An unknown page (0 or null) is never shown.
  const sourcePage =
    knownPage(item.source?.page) ??
    (item.snippet?.documentName === sourceDocName ? knownPage(item.snippet?.page) : undefined)
  const hasSourceRow = Boolean(sourceDocName)
  const snippetQuote = item.snippet?.quote

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground text-[10px] font-medium tracking-wider uppercase">
          {typeLabel}
        </span>
        <span className="text-foreground text-xs font-semibold tabular-nums">{item.id}</span>
      </div>

      <p className="text-foreground text-xs leading-relaxed">{item.label}</p>

      {hasBadges && (
        <div className="flex flex-wrap items-center gap-1.5">
          {item.category && (
            <Badge className="h-4 px-1.5 text-[10px]" variant="secondary">
              {item.category}
            </Badge>
          )}
          {item.isMandatory && (
            <Badge
              className="border-border bg-muted/50 text-muted-foreground h-4 px-1.5 text-[10px]"
              variant="outline"
            >
              {t("tender.extractionCitation.skaKrav")}
            </Badge>
          )}
          {riskClass && riskLabel && (
            <Badge className={cn("h-4 px-1.5 text-[10px]", riskClass)} variant="outline">
              {riskLabel}
            </Badge>
          )}
          {urgencyClass && urgencyLabel && (
            <Badge className={cn("h-4 px-1.5 text-[10px]", urgencyClass)} variant="outline">
              {urgencyLabel}
            </Badge>
          )}
          {item.extra?.weightPercent != null && (
            <Badge className="h-4 px-1.5 text-[10px]" variant="outline">
              {item.extra.weightPercent as number}%
            </Badge>
          )}
          {item.extra?.quantity != null && (
            <Badge className="h-4 px-1.5 text-[10px]" variant="outline">
              {t("tender.extractionCitation.quantityUnit", {
                count: item.extra.quantity as number,
              })}
            </Badge>
          )}
          {item.extra?.date != null && (
            <span className="text-muted-foreground text-[11px]">{item.extra.date as string}</span>
          )}
        </div>
      )}

      {(hasSourceRow || snippetQuote) && (
        <div className="border-border/50 flex flex-col gap-2 border-t pt-2">
          {hasSourceRow && (
            <SourceButton
              documentName={sourceDocName!}
              navigableTo={navigableSource}
              page={sourcePage}
              onOpenSource={onOpenSource}
            />
          )}
          {snippetQuote && (
            <div className="border-border/40 border-l-2 py-0.5 pl-3">
              <p className="text-muted-foreground line-clamp-3 text-[11px] leading-relaxed italic">
                "{snippetQuote}"
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Citation pill ────────────────────────────────────────────────────────────

function ExtractionCitationPill({
  id,
  item,
  onOpenSource,
}: {
  id: string
  item: ExtractionItem | undefined
  onOpenSource?: OpenSourceFn
}) {
  const { t } = useTranslation()
  if (!item) {
    return (
      <span
        className="border-border text-muted-foreground inline-flex items-center rounded border px-1.5 py-px text-[10px] font-medium opacity-60"
        title={t("tender.extractionCitation.missingItemTitle", { id })}
      >
        {id}
      </span>
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "h-auto rounded border px-1.5 py-px text-[10px] font-medium",
            "border-accent bg-accent/50 text-accent-foreground hover:bg-accent/20",
            "data-[state=open]:bg-accent/20"
          )}
        >
          {id}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="border-border/60 bg-background/80 w-[380px] p-4 shadow-xl backdrop-blur-md"
        sideOffset={6}
      >
        <ExtractionCitationCard item={item} onOpenSource={onOpenSource} />
      </PopoverContent>
    </Popover>
  )
}

// ── SourceSnippetPill — compact bid-document source reference ────────────────

function SourceSnippetCard({ snippet }: { snippet: ScoringSnippet }) {
  const { t } = useTranslation()
  const docName = snippet.documentName || t("tender.bidReview.defaultBidDocument")
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <FileText className="text-muted-foreground size-3.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-foreground truncate text-xs font-medium">{docName}</p>
          {knownPage(snippet.page) !== undefined && (
            <p className="text-muted-foreground text-[11px]">
              {t("tender.snippet.page", { page: snippet.page })}
            </p>
          )}
        </div>
      </div>
      {snippet.quote && (
        <div className="border-border/40 ml-1 border-l-2 py-1 pl-3">
          <p className="text-muted-foreground text-xs leading-relaxed italic">"{snippet.quote}"</p>
        </div>
      )}
    </div>
  )
}

export function SourceSnippetPill({ snippet }: { snippet: ScoringSnippet }) {
  const { t } = useTranslation()
  const baseName = snippet.documentName || t("tender.bidReview.defaultBidDocument")
  const label =
    knownPage(snippet.page) !== undefined
      ? `${baseName} ${t("tender.snippet.page", { page: snippet.page })}`
      : baseName

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "h-auto max-w-[200px] gap-1 rounded border px-1.5 py-0.5 text-[11px] font-medium",
            "border-border text-muted-foreground hover:bg-accent hover:text-foreground",
            "data-[state=open]:bg-accent data-[state=open]:text-foreground"
          )}
        >
          <FileText className="size-3 shrink-0" />
          <span className="truncate">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="border-border/60 bg-background/80 w-[360px] p-4 shadow-xl backdrop-blur-md"
        sideOffset={6}
      >
        <SourceSnippetCard snippet={snippet} />
      </PopoverContent>
    </Popover>
  )
}

// ── CitationText — the public integration component ──────────────────────────

type CitationTextProps = {
  text: string
  lookup: ExtractionItemLookup
  onOpenSource?: OpenSourceFn
  as?: "p" | "span" | "li"
  className?: string
}

export function CitationText({
  text,
  lookup,
  onOpenSource,
  as: Tag = "span",
  className,
}: CitationTextProps) {
  const parts = useMemo(() => parseExtractionCitations(text), [text])

  const hasCitations = parts.some((p) => p.type === "citation")
  if (!hasCitations) {
    return <Tag className={className}>{text}</Tag>
  }

  return (
    <Tag className={className}>
      {parts.map((part, i) => {
        if (part.type === "text") return <span key={i}>{part.value}</span>
        return (
          <ExtractionCitationPill
            key={i}
            id={part.id}
            item={lookup.get(normalizeCitationId(part.id))}
            onOpenSource={onOpenSource}
          />
        )
      })}
    </Tag>
  )
}
